import { Box, Button, Stack, Typography } from '@mui/material'
import { IconButton } from 'utoniq-ui'

import { Maybe, PaymentMethod } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { Loading } from '@/components/organisms'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  artistId: string
  paymentMethod: Maybe<PaymentMethod>
  formattedExpiredAt: Maybe<string>
  voucherUrl: Maybe<string>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const ArtistMembershipCompletionTemplate: Template<State, Action> = ({
  state,
}) => {
  const { t } = useTranslation('artistMembershipCompletion')
  const { routerPages, generatePath, routerNames, router } = useRouter()

  return (
    <ArtistThemeProvider>
      <Head
        title={
          state.paymentMethod === PaymentMethod.Konbini
            ? routerNames.ArtistMembershipKonbiniCompletion
            : routerNames.ArtistMembershipCompletion
        }
      />
      <BaseLayout
        margin
        header={{
          title: t(`${state.paymentMethod}Title`),
          left: (
            <IconButton.Close
              edge="start"
              onClick={() => {
                router.goBack()
              }}
            />
          ),
        }}
      >
        {state.loading ? (
          <LoadingCell />
        ) : (
          <>
            <Box sx={{ pt: 7, pb: 6 }}>
              <Typography variant="h5" align="center">
                {t(`${state.paymentMethod}SuccessTitle`)}
              </Typography>
            </Box>
            <Typography variant="body2">
              {t(`${state.paymentMethod}SuccessDesc`)}
            </Typography>
            {state.paymentMethod === PaymentMethod.Konbini && (
              <Stack
                sx={{
                  mt: 5,
                  py: 2,
                  borderColor: 'error.main',
                  borderWidth: 1,
                  borderStyle: 'solid',
                }}
              >
                <Typography variant="subtitle1" color="error" align="center">
                  {t('paymentDeadline', {
                    date: state.formattedExpiredAt ?? '',
                  })}
                </Typography>
              </Stack>
            )}
            <Stack
              justifyContent="center"
              alignItems="center"
              spacing={2}
              sx={{ py: 5, mt: 5 }}
            >
              {state.paymentMethod === PaymentMethod.CreditCard ? (
                <>
                  <Button
                    size="large"
                    href={routerPages.MembershipManagement}
                    sx={{ minWidth: 244 }}
                  >
                    {t('viewMembershipManagement', 'メンバーシップ管理を見る')}
                  </Button>
                  <Button
                    variant="text"
                    href={generatePath(routerPages.ArtistTop, {
                      artistId: state.artistId,
                    })}
                    sx={{ minWidth: 244 }}
                  >
                    {t('viewTOP', 'TOPページへ')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    component="a"
                    target="_blank"
                    size="large"
                    href={state.voucherUrl ?? '#'}
                    sx={{ minWidth: 244 }}
                  >
                    {t('proceedPaymentProcedure', '決済手順へすすむ')}
                  </Button>
                  <Button
                    variant="text"
                    href={routerPages.PaymentStatuses}
                    sx={{ minWidth: 244 }}
                  >
                    {t('checkPaymentStatus', '決済状況を確認する')}
                  </Button>
                </>
              )}
            </Stack>
          </>
        )}
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const LoadingCell: Cell = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '90dvh',
      }}
    >
      <Loading />
    </Box>
  )
}
